import { inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import groupBy from 'lodash-es/groupBy';
import isArray from 'lodash-es/isArray';
import { combineLatest, EMPTY, first, map, Observable, switchMap, tap } from 'rxjs';

import { runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { CanvasElement, getFormGroupLikeWrapper } from '@blaze-runtime/engine-domain';
import {
  DatasetScratchApiService,
  Scratch,
  ScratchTable,
} from '@blaze-shared/datasets-data-access';
import { DataBindingSourceInfo } from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { isDefined } from '@blaze-shared/shared-util';

import { getElementEntities } from '../+state/elements/elements.selectors';
import {
  isEditableForm,
  isFormGroupLike,
} from '../canvas-elements/element-model/element-type-guards';
import { getWrapperRefs } from '../canvas-elements/element-model/wrapper-refs';

import { ElementDefinitionsService } from './element-definitions.service';

export type FormGroupWorkflowNodeType =
  | 'upsert' // insert or update a single row
  | 'merge' // insert/update/delete multiple rows
  | 'assign' // assign affected ids into workflow variable
  | 'postmerge' // update incoming key after merge (because merge cannot)
  | 'ghostbusters' // get rid of deleted rows
  | 'singularize' // fetch affected row ids into an array
  | 'optionalize' // support optional forms
  | 'assign_optional'; // assign affected ids from optional branch

@Injectable({ providedIn: 'root' })
export class FormGroupUtilsService {
  private schemaService = inject(SchemaService);
  private store = inject(Store);
  private datasetScratchApiService = inject(DatasetScratchApiService);
  private elementDefinitionsService = inject(ElementDefinitionsService);

  workflowNodeId(type: FormGroupWorkflowNodeType, dataSource: DataBindingSourceInfo): string {
    const datasetId = this.schemaService.getDatasetId(dataSource.id, dataSource.sourceType);
    return `action_${type}_${datasetId}`;
  }

  saveDraft(formGroupLike$: Observable<CanvasElement>, elementId: string): Observable<Scratch> {
    return combineLatest({
      element: formGroupLike$,
      data: this.readDraft(formGroupLike$),
      filledSteps: this.store
        .select(runtimeSelectors.selectElementMetaDataFeaturesById(elementId))
        .pipe(
          map((elementMeta) =>
            elementMeta && elementMeta.elementType === 'wizard'
              ? elementMeta.filledSteps
              : undefined,
          ),
        ),
    }).pipe(
      first(),
      map(
        ({ element, data, filledSteps }) =>
          <Scratch>{
            componentId: element.id,
            data,
            metadata: { filledSteps },
          },
      ),
      switchMap((scratch) => this.datasetScratchApiService.save(scratch)),
    );
  }

  loadDraft(formGroupLike$: Observable<CanvasElement>, elementId: string): Observable<Scratch> {
    return formGroupLike$.pipe(
      first(),
      switchMap((formGroupLike) => this.datasetScratchApiService.get(formGroupLike.id)),
      tap(({ data, metadata }) => {
        this.store.dispatch(
          runtimeActions.updateElementMeta({
            elementId,
            meta: {
              dataFeatures: {
                elementType: 'wizard',
                filledSteps: metadata?.filledSteps,
              },
            },
          }),
        );
        this.writeDraft(formGroupLike$, data);
      }),
    );
  }

  private readDraft(formGroupLike$: Observable<CanvasElement>): Observable<ScratchTable[]> {
    return formGroupLike$.pipe(
      first(),
      switchMap((formGroupLike) => {
        if (!isFormGroupLike(formGroupLike)) {
          return EMPTY;
        }
        const wrapper = getFormGroupLikeWrapper(getWrapperRefs(formGroupLike));
        return combineLatest(
          wrapper.map(({ formId, datasetId, keyColumnId }) =>
            this.store
              .select(
                runtimeSelectors.selectOutputUiDataById(
                  formId,
                  this.elementDefinitionsService.getDefaultOutputEventType(formGroupLike.type),
                ),
              )
              .pipe(
                map((data) => ({
                  formId,
                  datasetId,
                  keyColumnId,
                  data: isArray(data) ? data : [data],
                })),
                first(),
              ),
          ),
        ).pipe(
          concatLatestFrom(() => this.store.select(getElementEntities)),
          map(([results, elements]) => {
            const full = results
              .map(({ formId, datasetId, keyColumnId, data }) => {
                const form = elements[formId];
                if (!isEditableForm(form)) {
                  return { datasetId, keyColumnId, data };
                } else {
                  return undefined;
                }
              })
              .filter(isDefined);
            const partitioned = results
              .map(({ formId, datasetId, keyColumnId, data }) => {
                const form = elements[formId];
                if (isEditableForm(form)) {
                  return {
                    datasetId,
                    keyColumnId,
                    data:
                      form.props.content.model
                        ?.filter(({ visible }) => visible)
                        .map(({ field }) => [field, data[0][field]] as const) ?? [],
                  };
                } else {
                  return undefined;
                }
              })
              .filter(isDefined);
            const merged = Object.entries(
              groupBy(partitioned, ({ datasetId, keyColumnId }) => `${datasetId}-${keyColumnId}`),
            ).map(([, partitions]) => {
              return {
                datasetId: partitions[0].datasetId,
                keyColumnId: partitions[0].keyColumnId,
                data: [Object.fromEntries(partitions.flatMap(({ data }) => data))],
              };
            });
            return [...full, ...merged].map(({ datasetId, keyColumnId, data }) => ({
              datasetId,
              columnId: keyColumnId ?? null,
              values: data,
            }));
          }),
        );
      }),
    );
  }

  private writeDraft(formGroupLike$: Observable<CanvasElement>, data: ScratchTable[]): void {
    formGroupLike$
      .pipe(
        first(),
        concatLatestFrom(() => this.store.select(getElementEntities)),
        switchMap(([formGroupLike, elements]) => {
          const draft = Object.fromEntries(
            Object.entries(
              groupBy(
                getFormGroupLikeWrapper(getWrapperRefs(formGroupLike)),
                ({ datasetId }) => datasetId,
              ),
            ).flatMap(([datasetId, forms]) =>
              forms
                .map<[string, Record<string, any>[]] | undefined>(({ formId }) => {
                  const form = elements[formId];
                  const values = data.find((entry) => entry.datasetId === datasetId)?.values;
                  if (!values) {
                    return undefined;
                  }
                  if (isEditableForm(form)) {
                    const partition = Object.fromEntries(
                      form.props.content.model
                        ?.filter(({ visible }) => visible)
                        .map(({ field }) => [field, values?.[0][field]]) ?? [],
                    );
                    return [formId, [partition]];
                  } else {
                    return [formId, values];
                  }
                })
                .filter(isDefined),
            ),
          );
          this.store.dispatch(
            runtimeActions.updateElementMeta({
              elementId: formGroupLike.id,
              meta: {
                dataFeatures: {
                  elementType: 'wizard',
                  draft,
                },
              },
            }),
          );
          return EMPTY;
        }),
      )
      .subscribe();
  }
}
